import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText,
  Radio, RadioGroup, Snackbar, TextField,
} from '@mui/material';
import { formatBRL } from '../../UI';
import { aprovadaSimulacao, desaprovarSimulacao } from '../../../../services/core/processos/processosAcordosService';
import { isBadRequestException, haveExceptionValidations } from '../../../../helpers/utils/utils';
import { makeErrorField, updateFieldsWithErrors, extractResponseErrorMessage } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';
import { FORMA_PAGAMENTO_PARCELA } from './acordosController';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível registrar a análise. Tente novamente.';

function formatarResumoParcelas(parcelas) {
  if (!parcelas || parcelas.length === 0) {
    return 'Nenhuma parcela.';
  }
  return parcelas.map((parcela, index) => (
    `Parcela ${parcela.nroParcela ?? index + 1}: ${FORMA_PAGAMENTO_PARCELA[parcela.idTipoFormaPagto] ?? parcela.idTipoFormaPagto}`
    + ` - Vencimento: ${parcela.dataVencimento ?? '—'}`
    + ` - Valor: ${parcela.valor == null ? '—' : formatBRL(parcela.valor)}`
  )).join('\n');
}

/**
 * Dialog "Relógio" (Análise da Proposta/Simulação, AW2-1133 Etapa 10) — porta
 * de `DialogAnaliseSimulacao.jsx` real. Divergência confirmada por leitura
 * direta do código-fonte real em relação ao texto do plano de arquitetura
 * (que citava `persistirAnaliseSimulacao`): o componente real só chama
 * `aprovadaSimulacao`/`desaprovarSimulacao` — `persistirAnaliseSimulacao`
 * existe em `dialogAnaliseSimulacaoController.js` mas está morto, nenhum
 * consumidor real o invoca.
 *
 * Simplificações deliberadas em relação à tela real (fora do critério de
 * pronto desta etapa, que fala só de "aprovar/desaprovar/persistir
 * análise reproduz o mesmo fluxo e mensagens"):
 * - As 4 abas reais (Último Histórico, Acompanhamento do Processo, Proposta
 *   de Acordos/títulos, Parcelas) foram reduzidas a 2 blocos sempre
 *   visíveis (Histórico + Parcelas), sem chrome de abas — "Acompanhamento
 *   do Processo" embutiria uma tela inteira de produção em MUI v4,
 *   proibido pela regra de isolamento de stack (`src/prototipo/CLAUDE.md`);
 *   "Proposta de Acordos" exigiria um fetch novo
 *   (`findTitulosAcordosProcessoService`) não confirmado como necessário
 *   para o critério de pronto.
 * - Fluxo "Contraproposta abre Novo Acordo" (`setOpenButtonNovoAcordo`/
 *   `setIsContraProposta` reais) NÃO replicado como navegação automática —
 *   cruzaria para a aba Acompanhamento (`NovoAcordo.jsx`, AW2-1032), fora do
 *   limite desta dialog. O payload/chamada de backend (`desaprovarSimulacao`
 *   com `simulacaoAprovada: 'CP'`) é idêntico à produção. Decisão confirmada
 *   com o usuário (achado em code review): em vez de integrar as 2 abas,
 *   `sufixoContraproposta` (`onSubmit`) acrescenta uma orientação textual ao
 *   Snackbar de resultado, guiando o usuário ao próximo passo manual.
 * - `dispatch(findQuantidadeAutorizacoesAction())` real (atualiza badge do
 *   Topbar) não replicado — o Topbar do protótipo já popula esse badge no
 *   próprio mount (ver `src/prototipo/CLAUDE.md`), sem consumidor aqui.
 *
 * Fail-open na tela real (achado ao ler o código-fonte, corrigido aqui): o
 * `catch` de `onClickSubmitAnalise` real só trata `isBadRequestException` —
 * qualquer outro erro (rede, 500) cai em SILÊNCIO TOTAL, sem toast nem log.
 * Aqui um fallback genérico (`MENSAGEM_ERRO_GENERICA`) cobre esse caso.
 */
export default function DialogAnaliseSimulacao({
  open, acordo, idProcesso, onClose, onMutado,
}) {
  const [simulacaoAprovada, setSimulacaoAprovada] = useState('');
  const [textoJustificativa, setTextoJustificativa] = useState('');
  const [errorFields, setErrorFields] = useState({
    simulacaoAprovada: makeErrorField(),
    textoJustificativa: makeErrorField(),
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setSimulacaoAprovada('');
    setTextoJustificativa('');
    setErrorFields({ simulacaoAprovada: makeErrorField(), textoJustificativa: makeErrorField() });
    setEnviando(false);
    setErro('');
    setResultado(null);
  });

  const onFecharResultado = () => setResultado(null);
  const onFecharErro = () => setErro('');

  const onChangeSimulacaoAprovada = (e) => {
    const { value } = e.target;
    setSimulacaoAprovada(value);
    setErrorFields(atual => ({ ...atual, simulacaoAprovada: makeErrorField() }));
  };

  const onChangeTextoJustificativa = (e) => {
    const { value } = e.target;
    setTextoJustificativa(value);
    setErrorFields(atual => ({ ...atual, textoJustificativa: makeErrorField() }));
  };

  const onSubmit = async () => {
    if (enviandoRef.current || !acordo || !simulacaoAprovada) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    const payload = { simulacaoAprovada, textoJustificativa };
    const isAprovada = simulacaoAprovada === 'A';
    const isContraproposta = simulacaoAprovada === 'CP';
    enviandoRef.current = true;
    setEnviando(true);

    try {
      const { data } = isAprovada
        ? await aprovadaSimulacao(payload, acordo.id)
        : await desaprovarSimulacao(payload, acordo.id);
      const { codResult, msgResult } = data;

      if (sessaoRef.current === sessaoAlvo) {
        onClose();
        // Produção abre o wizard "Novo Acordo" automaticamente após
        // Contraproposta (`setOpenButtonNovoAcordo`/`setIsContraProposta`,
        // incondicional a `codResult`) — esse wizard vive na aba
        // Acompanhamento (`NovoAcordo.jsx`, AW2-1032), fora do alcance desta
        // dialog. Decisão confirmada com o usuário: em vez da integração
        // entre abas, o sufixo de orientação abaixo fecha a lacuna de
        // descoberta (a chamada ao backend já foi feita corretamente; falta
        // só guiar o usuário ao próximo passo manual).
        const sufixoContraproposta = isContraproposta
          ? ' Vá até a aba Acompanhamento para registrar o Novo Acordo (Contraproposta).'
          : '';
        if (codResult === 0) {
          setResultado({ severidade: 'success', mensagem: `Acordo gerado com sucesso.${sufixoContraproposta}` });
        } else {
          setResultado({ severidade: codResult === 1 ? 'warning' : 'error', mensagem: `${msgResult}${sufixoContraproposta}` });
        }
      }
      if (onMutado) {
        onMutado();
      }
    } catch (exception) {
      if (sessaoRef.current === sessaoAlvo) {
        if (isBadRequestException(exception) && haveExceptionValidations(exception)) {
          const [atualizados, naoMapeados] = updateFieldsWithErrors(exception, {
            simulacaoAprovada: makeErrorField(),
            textoJustificativa: makeErrorField(),
          });
          const temErroVisivel = Object.values(atualizados).some(campo => campo.error);
          if (temErroVisivel) {
            setErrorFields(atualizados);
            if (naoMapeados.length > 0) {
              setErro(naoMapeados.map(item => item.message).join(' '));
            }
          } else {
            setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
          }
        } else if (isBadRequestException(exception) && exception?.response?.data?.message) {
          onClose();
          setErro(exception.response.data.message);
          if (onMutado) {
            onMutado();
          }
        } else {
          setErro(MENSAGEM_ERRO_GENERICA);
        }
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Análise da Proposta</DialogTitle>
        <DialogContent>
          <TextField
            label="Histórico"
            value={acordo?.texto ?? ''}
            multiline
            rows={4}
            fullWidth
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            label="Parcelas"
            value={formatarResumoParcelas(acordo?.parcelas)}
            multiline
            rows={4}
            fullWidth
            disabled
            sx={{ mb: 2 }}
          />
          <FormControl error={errorFields.simulacaoAprovada.error} disabled={enviando} sx={{ mb: 2 }}>
            <RadioGroup row value={simulacaoAprovada} onChange={onChangeSimulacaoAprovada}>
              <FormControlLabel value="A" control={<Radio />} label="Aprovada" />
              <FormControlLabel value="NA" control={<Radio />} label="Não Aprovada" />
              <FormControlLabel value="CP" control={<Radio />} label="Contraproposta" />
            </RadioGroup>
            {errorFields.simulacaoAprovada.error && (
              <FormHelperText>{errorFields.simulacaoAprovada.errorMessage}</FormHelperText>
            )}
          </FormControl>
          <TextField
            label="Justificativa"
            value={textoJustificativa}
            onChange={onChangeTextoJustificativa}
            disabled={enviando}
            multiline
            rows={4}
            fullWidth
            error={errorFields.textoJustificativa.error}
            helperText={errorFields.textoJustificativa.errorMessage}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onSubmit} variant="contained" disabled={enviando || !simulacaoAprovada}>OK</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={onFecharErro}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErro}>
          {erro}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(resultado)}
        autoHideDuration={6000}
        onClose={onFecharResultado}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={resultado?.severidade ?? 'info'} variant="filled" onClose={onFecharResultado}>
          {resultado?.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogAnaliseSimulacao.propTypes = {
  open: PropTypes.bool,
  acordo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    texto: PropTypes.string,
    parcelas: PropTypes.array,
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onMutado: PropTypes.func,
};

DialogAnaliseSimulacao.defaultProps = {
  open: false,
  acordo: null,
  idProcesso: null,
  onMutado: null,
};
